import { getBlocksDataFromSectionData } from '@/utils/customisation'
import { cn } from 'oddle-shop-monorepo.design-system'
import React from 'react'
import { useSelector } from 'react-redux'
import ResponsiveSection from 'src/components/common/ResponsiveSection'
import { selectSections } from 'src/redux/customisation'
import SlideshowCarousel from './SlideshowCarousel'
import { defaultImageSource } from '@/libs/config/client'

interface SlideShowBannerSection {
  id: string
  name: string
  type: string
  settings?: {
    autoscroll: boolean
    autoscrollInterval: number
  }
  blocks?: {
    [key: string]: Customisation.Block
  }
  blocksOrder: string[]
  updatedAt: number
  options: {
    visibleForPreview: boolean
  }
}

interface SlideShowBannerProps {
  id: string
  index: number
}

type BlockData = NonNullable<SlideShowBannerSection['blocks']>[string]

const SlideshowBanner = React.memo(
  ({ id, index }: SlideShowBannerProps): JSX.Element | null => {
    const customisation = useSelector(selectSections)[id] as
      | SlideShowBannerSection
      | undefined
    const containerRef = React.useRef<HTMLDivElement>(null)

    // Null-tolerant: with the store seeded during SSR, a section id missing
    // from the seeded page data must not crash the server render. All hooks
    // still run unconditionally (rules of hooks); the early return happens
    // after them, just before the JSX.
    const settings = customisation?.settings
    const images = React.useMemo(
      () =>
        customisation
          ? getBlocksDataFromSectionData(customisation)
              .filter((block): block is BlockData => block !== undefined)
              .map((block) => ({
                src: block.settings.imageSource || defaultImageSource,
                ...block.settings,
              }))
          : [],
      [customisation],
    )

    const autoplay = images.length > 1 ? settings?.autoscroll : false

    const onVisibilityChange = React.useCallback((visible: boolean) => {
      // Embla carousel handles autoplay internally, no need for manual control
    }, [])

    React.useEffect(() => {
      if (!autoplay || !containerRef.current) return

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry) {
            onVisibilityChange(entry.isIntersecting)
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
        },
      )

      observer.observe(containerRef.current)

      return () => {
        observer.disconnect()
      }
    }, [autoplay, onVisibilityChange])

    if (!customisation) return null

    return (
      <div ref={containerRef}>
        <ResponsiveSection className={cn('mb-0 mt-0 lg:mt-[30px]')}>
          <ResponsiveSection.Container className="max-w-[100vw] px-0">
            <div className="relative w-full rounded-2xl shadow-sm md:shadow-none">
              <div className="w-full rounded-2xl">
                <SlideshowCarousel
                  autoplay={autoplay}
                  autoplayInterval={settings?.autoscrollInterval || 5000}
                  loop={true}
                  className="!mx-0"
                  images={images}
                />
              </div>
            </div>
          </ResponsiveSection.Container>
        </ResponsiveSection>
      </div>
    )
  },
)

SlideshowBanner.displayName = 'SlideshowBanner'

export default SlideshowBanner
